import React, { createContext, useContext, useEffect, useState } from 'react';
import { useParams, useLocation } from 'react-router-dom';
import { getPublicBrand, getBrandByHost } from '../lib/api';

const BrandCtx = createContext({ brand: null, mode: 'platform', loading: true });

// hostnames that are always the platform (not a custom domain)
const PLATFORM_HOST_HINTS = [
  'localhost',
  '127.0.0.1',
  'tecsuite.it',
  'push.tecsuite.it',
  'tecnologieverona.it',
  'emergent',
  'preview.emergentagent',
];

function isPlatformHost(hostname) {
  const h = (hostname || '').toLowerCase();
  return PLATFORM_HOST_HINTS.some((hint) => h.includes(hint));
}

export function BrandProvider({ children }) {
  const [brand, setBrand] = useState(null);
  const [mode, setMode] = useState('platform'); // 'platform' | 'branded-path' | 'branded-host'
  const [loading, setLoading] = useState(true);
  const location = useLocation();

  // Path-based slug detection: /b/:slug/...
  const pathSlugMatch = location.pathname.match(/^\/b\/([a-z0-9-]+)/i);
  const pathSlug = pathSlugMatch ? pathSlugMatch[1] : null;

  useEffect(() => {
    let cancelled = false;
    (async () => {
      setLoading(true);
      try {
        // 1. If URL has /b/:slug prefix, load that brand
        if (pathSlug) {
          try {
            const b = await getPublicBrand(pathSlug);
            if (!cancelled) { setBrand(b); setMode('branded-path'); }
          } catch { if (!cancelled) { setBrand(null); setMode('platform'); } }
          return;
        }

        // 2. Otherwise, check if we're on a custom domain
        const host = window.location.host.toLowerCase();
        if (!isPlatformHost(host)) {
          try {
            const res = await getBrandByHost(host);
            if (!cancelled && res.brand) {
              setBrand(res.brand);
              setMode('branded-host');
              return;
            }
          } catch { /* fall through to platform */ }
        }
        if (!cancelled) { setBrand(null); setMode('platform'); }
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [pathSlug]);

  // Update document title based on brand
  useEffect(() => {
    if (brand) {
      document.title = `${brand.name} · Secure Push`;
    } else {
      document.title = 'Secure Push';
    }
  }, [brand]);

  return (
    <BrandCtx.Provider value={{ brand, mode, loading, isBranded: !!brand, pathSlug }}>
      {children}
    </BrandCtx.Provider>
  );
}

export function useBrand() {
  return useContext(BrandCtx);
}
